<template>
  <b-container fluid class="top-padding bottom-padding no-horiz-padding">
    <h2>Draft Color Editor Instructions</h2>
    <div>
      Use the Color Editor to experiment with different colors for any draft!  You must be signed in to Handweaving.net as a subscriber to use this feature.
    </div>
    <div style='margin-top: 1rem'>
      <b-button variant='primary' style='vertical-align: top' size='sm' target='_blank' href='https://youtu.be/EeR1GNLPXjk'>Video Demo »</b-button>
    </div>
    <hr>
    <h2 class='top-margin'>Features Overview</h2>

    <div>
      <ul>

        <li>Try different colors in the warp and/or weft of any draft, both those on Handweaving.net as well as your own.</li>

        <li>Select new colors manually or from an image that you upload.</li>

        <li>Assign colors thread by thread or by replacing all of the same color within the warp or weft.</li>

        <li>Download your draft colorations in WIF format or as images.</li>
      </ul>
    </div>

    <hr>
    <h2 class='top-margin'>Loading Drafts from Handweaving.net</h2>

    <div>
      To experiment with colors using any draft on Handweaving.net, click on the Actions button on any draft detail page and then on the Color button.  Changes that you make to the draft in the editor will not affect the original draft on the site.
    </div>

    <div>
      <img style='max-width: 75%' :src='this.getSiteImageUrl("color-editor-draft.png")' />
    </div>

    <hr>
    <h2 class='top-margin'>Loading Your Own Drafts from WIF Files</h2>

    <div>
      To change the colors on any of your own drafts, click the Drafts menu at the top, then choose Your Drafts, 
      then click on the Upload button, then choose a WIF file from your computer.  
      Drafts that you upload into the Color Editor are not stored on Handweaving.net.  
    </div>

    <div>
      <img :src='this.getSiteImageUrl("color-editor-your-draft.png")' /><br>
      <img style='max-width: 40%' :src='this.getSiteImageUrl("color-editor-upload-wif.png")' />
    </div>

    <hr>
    <h2 class='top-margin'>Choosing the Color Palette</h2>

    <div>
      When you load a draft, the colors found within it are shown one by one in a palette at the top.  You can change these colors and also add more.  Colors from the palette are used to color the warp and weft threads in the draft.
      One of the colors is always selected and shown in a larger box than the others.  You can select a different color by clicking on any of the color boxes.
      If you need more color entries in the palette, click the Plus + Icon to add them.
    </div>

    <div>
      <img style='max-width: 75%' :src='this.getSiteImageUrl("color-editor-palette.png")' />
    </div>

    <div>
      To edit a color directly, click on the pencil icon above it to show a Color Picker. 
    </div>

    <div>
      <img style='max-width: 75%' :src='this.getSiteImageUrl("color-editor-pencil.png")' />
    </div>

    <div>
      Then click the hue column in the center of the Color Picker to show all of the colors close to that hue, 
      then drag the color selector circle around within those colors to choose one.  Finally click OK.
    </div>

    <div>
      <img :src='this.getSiteImageUrl("color-editor-color-picker.png")' />
    </div>

    <div>
      To choose a color from an image, click the Select Colors from Image button, upload your image.
    </div>

    <div>
      <img style='max-width: 75%' :src='this.getSiteImageUrl("color-editor-select-from-image.png")' />
    </div>

    <div>
      After the image loads, click anywhere within it to change the selected colors (the one with the larger box), to the color from where you clicked.  You can fine tune these by editing them directly later using the pencil icon if needed.
    </div>

    <div>
      <img :src='this.getSiteImageUrl("color-editor-choose-from-image.png")' />
    </div>

    <div>
      When you load an image, you can click the Hide Image button to hide it when done choosing colors, and also the Show Image button later if you want to see it again and/or choose more colors from it.
    </div>

    <hr>
    <h2 class='top-margin'>Coloring a Draft</h2>

    <div>
      To set warp or weft colors, click on one of the colors in the palette to select it, then click on any thread color in the warp or weft to change it to the selected color.  You can also click and drag across the warp or weft colors to change a group of threads all at once.
    </div>

    <div>
      <img style='max-width: 75%' :src='this.getSiteImageUrl("color-editor-before-cleaned.png")' />
    </div>

    <div>
      To search and replace all of the threads of the same color in the warp or weft with the selected color, click the Replace Colors button.  Then click on any thread in the warp or weft.  All of the threads of the color you clicked in the warp will be replaced by the selected color (or in the weft if you clicked a weft thread).
    </div>

    <div>
      <img style='max-width: 75%' :src='this.getSiteImageUrl("color-editor-search-replace-colors-before.png")' />
    </div>

    <div>
      When you are done searching and replacing colors, click the Done Replacing Colors button.  After this you can set the colors on threads individually again like before by clicking or dragging.
    </div>

    <div>
      <img :src='this.getSiteImageUrl("color-editor-done-replacing-colors.png")' />
    </div>

    <hr>
    <h2 class='top-margin'>Choosing the View Size, Showing and Hiding Lines</h2>

    <div>
      Drag the Size slider to the right to make the draft appear larger, and to the left to make it appear smaller.
      Lines are included in the draft to show the thread interlacement.  You can show or hide these using the Lines checkboxes.  Viewing the draft without the lines can make it easier to see the colors.
    </div>

    <div>
      <img :src='this.getSiteImageUrl("color-editor-size-lines.png")' />
    </div>
    
    <hr>
    <h2>Saving Your Work</h2>

    <div>
      The color editor does not store any edited drafts on Handweaving.net.  If you want to save your work, click the Download button.  
    </div>

    <div>
      <img style='max-width: 75%' :src='this.getSiteImageUrl("color-editor-download-button.png")' />
    </div>

    <div>
      You will be asked whether to download as a WIF or an image.
    </div>

    <div>
      <img :src='this.getSiteImageUrl("color-editor-download-dialog.png")' />
    </div>

    <div>
      If you choose WIF, a file will be downloaded to your computer which you can either load into your own weaving software, or use again later with the color editor by uploading it.
      If you choose PNG Image, a picture of the draft will be downloaded.  You can use this to print it or to post on a website or blog.  Use the Size slider before downloading the image to set the desired size for it.
    </div>
  </b-container>

</template>

<script>
import mailChimpSignup from './MailChimpSignup.vue'
import helpUsButton from './HelpUsButton.vue'
import mediaService from '../mixins/mediaService.js'
export default {
    mixins: [mediaService],
    components: {
        helpUsButton: helpUsButton,
        mailChimpSignup: mailChimpSignup
    },
    created() {
        this.$store.commit('setPageTitle', 'Draft Color Editor Instructions')
    }
}
</script>

<style scoped>
.maxLogoHeight {
    max-height: 90px;
    height: 90px;
}


img {
  max-width: 50%;
  margin-top: 1rem;
  margin-bottom: 1rem
}

.top-margin {
  margin-top: 1rem;
}

li {
  margin-top: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// src/components/DraftEditorInstructions.vue